/* Builder hooks. Local uses mocks; preview modes require a valid storefront theme context. */
(function (global) {
  'use strict';
  const PRODUCT_IDS = new Set(['6969285312595', '8009502818387']);
  const LOOPBACK = new Set(['localhost', '127.0.0.1', '[::1]']);
  // Stable model identity is independent of candidate slot and displayed rank.
  const ORDER_SERVICES = new Map([
    ['s3od-binary8k-e10', 'ninja_ai_8KBI'],
    ['s3od-binary51544-e40', 'ninja_ai_51KBI'],
    ['max-local-regular-e30', 'ninja_ai_40KRG'],
    ['s3od-removal22890-c10', 'ninja_ai_23KRO'],
    ['imgix-default', 'IX'],
    ['imgix-model-4', 'PC'],
    ['legacy-ideogram', 'Gram']
  ]);
  let runtime = null, assignment = null, config = null, mocks = null, draft = false, live = false, editor = false;
  const attempts = new Map();
  const stageAttempts = new Map();
  const uploadContexts = new Map();
  const cancelledStages = new Set();
  const revealObservers = new Map(), revealRuns = new Map(), revealsDone = new Map(), revealsFailed = new Map();
  const removalEnds = new Map(), previewEvidence = new Map();
  const clean = value => typeof value === 'string' && /^[A-Za-z0-9_.:-]{1,100}$/.test(value) ? value : undefined;
  const storageCodes=new Set(['AccessDenied','ExpiredToken','InvalidArgument','InvalidPolicyDocument','InvalidRequest',
    'SignatureDoesNotMatch','EntityTooLarge','EntityTooSmall','MalformedPOSTRequest','MaxPostPreDataLengthExceeded',
    'RequestTimeout','SlowDown','InternalError','ServiceUnavailable','RequestTimeTooSkewed']);
  const sourceExtensions=new Set(['jpg','jpeg','png','jfif','bmp','webp','heic','heif','avif','psd','gif','ai','pdf','svg','eps','tif','tiff','missing','other']);
  function storageErrorCode(xhr) {
    try {
      const body=xhr?.responseText;
      if(typeof body!=='string' || body.length>65536)return undefined;
      const value=body.match(/<Code>([A-Za-z]{1,64})<\/Code>/)?.[1];
      return storageCodes.has(value)?value:undefined;
    } catch (_) {return undefined;}
  }
  function failureDetail(id,stage,error,attemptId) {
    try {
      const supplied = stage === 'upload' && error?.diagnosticContext?.designId === id ? error.diagnosticContext.attemptId : undefined;
      const issued=attemptId || supplied || stageAttempts.get(`${id}:${stage}`);
      if(!runtime?.stagePending?.(id,issued,stage))return;
      const upload = stage === 'upload' ? uploadContexts.get(issued) : null;
      if (issued && (error?.failureKind === 'cancelled' || ['cancelled','upload_cancelled'].includes(error?.code))) cancelledStages.add(issued);
      record('fallback_detail',{...upload,designId:clean(id),attemptId:issued,stage,
        reason:clean(error?.code) || (stage==='cart'?'cart_request_failed':'upload_failed'),
        operation:clean(error?.operation),failureKind:clean(error?.failureKind) || 'unknown',
        errorName:clean(error?.errorName || error?.name),httpStatus:error?.httpStatus,requestId:clean(error?.requestId),
        measurementBuild:clean(error?.measurementBuild),measurementStep:clean(error?.measurementStep),
        measurementReason:clean(error?.measurementReason),
        timeoutMs:error?.timeoutMs,transportPhase:clean(error?.transportPhase),attemptNumber:error?.attemptNumber,
        timeoutKind:['upload_idle','upload_total'].includes(error?.timeoutKind)?error.timeoutKind:undefined,
        uploadLastProgressAgeMs:error?.uploadLastProgressAgeMs,
        storageCode:storageCodes.has(error?.storageCode)?error.storageCode:undefined,
        uploadTransport:['signed_post','legacy_put'].includes(error?.uploadTransport)?error.uploadTransport:undefined,
        uploadStatus:['stored','missing','mismatch','unavailable'].includes(error?.uploadStatus)?error.uploadStatus:undefined,
        sourceExtension:upload?.sourceExtension || (sourceExtensions.has(error?.sourceExtension)?error.sourceExtension:undefined),
        uploadBytesSent:error?.uploadBytesSent,uploadBytesTotal:error?.uploadBytesTotal});
    } catch (_) { /* Failure reporting cannot prevent the existing recovery or UI. */ }
  }
  function uploadContext(id, file) {
    try {
      const attemptId = stageAttempts.get(`${id}:upload`);
      if (!active() || !/^f-[0-9]{1,9}$/.test(id || '') || !runtime.stagePending(id,attemptId,'upload')) return;
      const ext = String(file?.name || '').includes('.') ? String(file.name).split('.').pop().toLowerCase() : 'missing';
      const knownMime = /^(?:image\/(?:png|jpeg|bmp|webp|heic|heif|avif|gif|tiff|svg\+xml|vnd\.adobe\.photoshop)|application\/(?:pdf|postscript))$/;
      const context = {designId:id,attemptId,sourceExtension:sourceExtensions.has(ext)?ext:'other',
        mimeState:!file?.type?'missing':knownMime.test(file.type)?'recognized':'other'};
      if (Number.isSafeInteger(file?.size) && file.size >= 0 && file.size <= 10995116277760) context.fileBytes=file.size;
      const snapshot = Object.freeze(context);
      if (uploadContexts.size >= 1000 && !uploadContexts.has(attemptId)) uploadContexts.delete(uploadContexts.keys().next().value);
      uploadContexts.set(attemptId,snapshot);
      return snapshot;
    } catch (_) { /* Diagnostic metadata must not affect upload admission. */ }
  }
  function draftScope(options) {
    return global.NtBgrProductionScope?.draftAllowed(options) === true;
  }
  function active() { return !!assignment && (editor ? global.NtBgrProductionScope?.editorAllowed(config) === true : live ? global.NtBgrProductionScope?.allowed(config) === true : draft ? draftScope(config) : LOOPBACK.has(global.location?.hostname)); }
  function treatment() { return active() && assignment.variation === 'ninja_ai'; }
  function record(name, fields) { if (active()) runtime.record(name, fields); }
  function status(value) {
    if (global.document?.documentElement) global.document.documentElement.dataset.ntBgrLocalStatus = value;
  }
  function boot(options = {}) {
    if (active()) return assignment;
    draft = draftScope(options);
    live = options.mode === 'live' && global.NtBgrProductionScope?.allowed(options) === true;
    editor = options.mode === 'editor' && global.NtBgrProductionScope?.editorAllowed(options) === true;
    if ((!draft && !live && !editor && !LOOPBACK.has(global.location?.hostname)) || options.enabled !== true) return null;
    let builderConfig;
    try { builderConfig = JSON.parse(global.document?.getElementById('bsv4-config')?.textContent || '{}'); }
    catch (_) { status('invalid-builder-config'); return null; }
    const productId = String(builderConfig.productId);
    if (!(options.activation === 'default' && global.NtBgrProductionScope?.productMatches(options) || PRODUCT_IDS.has(productId)) || String(options.productId) !== productId) {
      status('ineligible-product'); return null;
    }
    mocks = options.mocks || global.__ntBgrLocalMocks;
    if (!draft && !live && !editor && (mocks?.localOnly !== true || typeof mocks.uploadBlob !== 'function' ||
        typeof mocks.cart !== 'function' || typeof mocks.controlRun !== 'function' ||
        !['renderUrl', 'route', 'run', 'canFallback', 'failed'].every(key => typeof mocks.policy?.[key] === 'function'))) {
      status('missing-local-mocks');
      global.console?.error('Local BG experiment requires upload, cart, control, and removal-policy mocks. No experiment was started.');
      return null;
    }
    if (!global.NtBgrExperiment?.create) { status('missing-runtime'); return null; }
    config = options;
    runtime = global.NtBgrExperiment.create({...options,experimentKey: options.experimentKey || 'nt_bgr_v1',
      mode: editor ? 'editor' : live ? 'live' : draft ? 'draft' : 'local', themeId: options.themeId}, {onEvent:options.onEvent});
    assignment = runtime.assign(options.variation);
    if (!assignment) return null;
    global.__ntBgrExperiment = runtime;
    // No endpoint client is loaded. The adapter's routing-presence check uses this sentinel.
    if (!draft && !live && !editor) {
      global.__ninjaJobs = {localOnly: true};
      global.__ninjaRemovalPolicy = mocks.policy;
      global.__bsv4BgrRun = mocks.controlRun;
    }
    status('ready');
    return assignment;
  }
  function defaultFor(file, source = 'corners') {
    if (!active() || !file || file.ntAiAutoRoute || file.ntBgrDefaultEligible !== true) return;
    runtime.setDefault(file.id, !!file.bgRemove, source);
  }
  function routedDefault(file, source = 'router') {
    if (active() && file) runtime.setDefault(file.id, !!file.bgRemove, source);
  }
  function stopReveal(id) {
    revealObservers.get(id)?.disconnect(); revealObservers.delete(id);
    const run = revealRuns.get(id);
    if (run) run.cancelled = true;
    revealRuns.delete(id);
  }
  function toggle(file, on) {
    if (active() && file) { runtime.toggle(file.id, !!on); if (!on) { stopReveal(file.id); runtime.cancelOperation?.(file.id); } }
  }
  function requested(file) {
    if (!active() || !file) return;
    const attemptId = runtime.beginRemoval(file.id);
    stopReveal(file.id);
    attempts.set(file.id, attemptId);
    file.ntBgrAttemptId = attemptId;
    removalEnds.delete(file.id); previewEvidence.delete(file.id);
    return attemptId;
  }
  function durable(file, attemptId = file?.ntBgrAttemptId) {
    captureChoice(file);
    if (active() && file?.bgrModel && file.bgrVersions?.[file.bgrModel] && !file.ntAiPending) {
      runtime.durableReady(file.id, {attemptId});
      resolveRemoval(file, attemptId);
    }
  }
  function resolveRemoval(file, attemptId) {
    if (!active() || !file || !attemptId || attempts.get(file.id) !== attemptId || !file.bgRemove) return;
    const evidence = previewEvidence.get(file.id);
    if (evidence?.attemptId === attemptId && [...evidence.ready].some(([key,src]) =>
        file.bgrVersions?.[key] && (file.bgrDisplay?.[key] || file.bgrVersions[key]) === src)) {
      runtime.operationReady?.(file.id,attemptId);
      return;
    }
    const end = removalEnds.get(file.id);
    if (end?.attemptId !== attemptId) return;
    const keys = (file.bgrVisible || file.ntAiOrder || Object.keys(file.bgrVersions || {}))
      .filter(key=>file.bgrVersions?.[key]);
    // Only exhausted work, not a temporary reveal or a failed sibling, is terminal.
    if (!keys.length || evidence?.attemptId === attemptId && keys.every(key=>
        evidence.failed.get(key) === (file.bgrDisplay?.[key] || file.bgrVersions[key]))) {
      runtime.terminalFailure?.(file.id,attemptId,keys.length ? 'preview' : end.stage,
        keys.length ? 'preview_decode_failed' : end.reason);
    }
  }
  function removalFinished(file, attemptId, stage = 'removal', reason = 'unavailable') {
    try {
    if (!active() || !file || !attemptId || attempts.get(file.id) !== attemptId) return;
    if (!file.bgRemove || reason === 'cancelled') { runtime.cancelOperation?.(file.id); return; }
    removalEnds.set(file.id,{attemptId,stage,reason:clean(reason) || 'unavailable'});
    resolveRemoval(file,attemptId);
    // Successful fallback can contain only one choice; it still needs actual
    // displayed decode + a durable URL, independently of the three-choice timer.
    revealed(file,{attemptId})?.catch?.(()=>{});
    } catch (_) { /* Outcome reporting must never start a fallback or alter artwork. */ }
  }
  function revealed(file, fields = {}) {
    if (!active() || !file?.bgRemove) return;
    const attempt = fields.attemptId || file.ntBgrAttemptId;
    if (!attempt || attempt !== attempts.get(file.id)) return;
    if (revealsDone.get(file.id) === attempt) return;
    const previous = revealRuns.get(file.id);
    if (previous?.attempt === attempt) {
      previous.again = true;
      previous.fields = {...previous.fields, ...fields};
      return previous.promise;
    }
    const run = {attempt, fields, again:true, cancelled:false};
    revealRuns.set(file.id, run);
    const root = global.document?.querySelector?.('[data-bsv4-root]');
    if (root && !revealObservers.has(file.id)) {
      const changed = () => revealed(file, {...run.fields, attemptId:attempt});
      const observer = global.MutationObserver && new global.MutationObserver(changed);
      observer?.observe(root, {subtree:true, childList:true, attributes:true,
        attributeFilter:['class','src','hidden','disabled','aria-disabled']});
      root.addEventListener?.('load', changed, true);
      root.addEventListener?.('error', changed, true);
      revealObservers.set(file.id, {disconnect:() => {
        observer?.disconnect();
        root.removeEventListener?.('load', changed, true);
        root.removeEventListener?.('error', changed, true);
      }});
    }
    const current = () => !run.cancelled && active() && file.bgRemove && attempt === attempts.get(file.id) &&
      revealsDone.get(file.id) !== attempt;
    const isResult = (img, key) => {
      if (!treatment() || config?.builderEngine === 'stock') {
        const source = file.ntEffectKey && file.ntFxRestore ? file.ntFxRestore : file;
        const expected = source.bgrDisplay?.[key] || source.bgrVersions?.[key];
        if (typeof expected !== 'string' || !expected) return false;
        try { return img.src === new URL(expected, global.document?.baseURI || global.location?.href).href; }
        catch (_) { return false; }
      }
      const slot = img.closest('[data-bsv4-bgr-slot]'), state = slot?.__nttState;
      if (!state || state.lastOpts?.state !== 'ready' || state.shownUrl !== img.src) return false;
      return img.matches('.ntt-overlay.ntt-in') || (img.matches('.ntt-base') && !img.matches('.ntt-basefade'));
    };
    const capture = () => {
      if (treatment() && (!file.ntAiHasRevealed || file.ntAiPending)) return null;
      const keys = (file.ntBgrOptionKeys || (treatment() ? file.ntAiOrder : ['ix','pc','gram']))?.slice(0,3);
      if (!keys?.length || new Set(keys).size !== keys.length) return null;
      const hosts = [...(root?.querySelectorAll('[data-bsv4-bgr-for]') || [])]
        .filter(host => host.dataset.bsv4BgrFor === file.id);
      const images = hosts.flatMap(host => [...host.querySelectorAll('[data-bsv4-bgr-row] img')]);
      const rows = keys.map(key => {
        if (file.bgrPending?.[key]) return null;
        const candidates = images.filter(img => {
          const button = img.closest('button');
          return img.closest('[data-bsv4-bgr-slot]')?.dataset.bsv4BgrSlot === key &&
            img.isConnected && button && !button.disabled && button.getAttribute('aria-disabled') !== 'true' &&
            !img.closest('[hidden]') && img.getClientRects().length && isResult(img, key);
        });
        const img = candidates.find(img => img.complete && img.naturalWidth > 0) || candidates[0];
        return img && {key, img, src:img.src};
      });
      // Observe genuine failures even in a partial set, but never time it as all-three success.
      return {rows:rows.filter(Boolean), complete:keys.length === 3 && rows.every(Boolean)};
    };
    run.promise = (async () => {
      try {
        while (run.again && current()) {
          run.again = false;
          const before = capture();
          if (!before?.rows.length) return;
          // Record each completed candidate independently; a hung sibling must
          // not hide an already displayed, saved result from outcome reporting.
          const observeDecoded = (row, ok) => {
            if (!current() || !capture()?.rows.some(now=>now.key === row.key && now.img === row.img && now.src === row.src)) return;
            let evidence = previewEvidence.get(file.id);
            if (evidence?.attemptId !== attempt) {
              evidence = {attemptId:attempt,ready:new Map(),failed:new Map()};
              previewEvidence.set(file.id,evidence);
            }
            if (ok && row.img.complete && row.img.naturalWidth > 0) {
              evidence.ready.set(row.key,row.src); evidence.failed.delete(row.key);
            } else if (!ok || row.img.complete && !row.img.naturalWidth) {
              evidence.failed.set(row.key,row.src); evidence.ready.delete(row.key);
            }
            resolveRemoval(file,attempt);
          };
          const decoded = await Promise.allSettled(before.rows.map(row => Promise.resolve().then(() => row.img.decode?.())
            .then(value=>{observeDecoded(row,true);return value;},error=>{observeDecoded(row,false);throw error;})));
          await new Promise(resolve => (global.requestAnimationFrame || (fn => global.setTimeout(fn, 0)))(resolve));
          if (!current()) return;
          // Re-read the current picker. A replaced, hidden, or detached image is not a decode failure.
          const after = capture();
          if (!after?.rows.length) return;
          if (after.rows.length !== before.rows.length || after.rows.some((row, i) =>
              row.key !== before.rows[i].key || row.img !== before.rows[i].img || row.src !== before.rows[i].src)) {
            run.again = true;
            continue;
          }
          const ready = after.rows.every(({img}) => img.complete && img.naturalWidth > 0);
          let evidence = previewEvidence.get(file.id);
          if (evidence?.attemptId !== attempt) {
            evidence = {attemptId:attempt,ready:new Map(),failed:new Map()};
            previewEvidence.set(file.id,evidence);
          }
          after.rows.forEach(({key,img,src},i) => {
            if (decoded[i].status === 'fulfilled' && img.complete && img.naturalWidth > 0) {
              evidence.ready.set(key,src); evidence.failed.delete(key);
            } else if (decoded[i].status === 'rejected' || img.complete && !img.naturalWidth) {
              evidence.failed.set(key,src); evidence.ready.delete(key);
            }
          });
          resolveRemoval(file,attempt);
          if (!ready) {
            const failedIndex=after.rows.findIndex(({img}, i) => !(img.complete && img.naturalWidth > 0) &&
                (decoded[i].status === 'rejected' || img.complete && !img.naturalWidth));
            if (failedIndex>=0 && revealsFailed.get(file.id) !== attempt) {
              runtime.failure(file.id, attempt, 'preview', 'preview_decode_failed');
              record('fallback_detail',{designId:file.id,attemptId:attempt,stage:'preview',reason:'preview_decode_failed',
                source:['ix','pc','gram'].includes(after.rows[failedIndex].key)?after.rows[failedIndex].key:'initial',
                operation:'preview_decode',failureKind:decoded[failedIndex].status==='rejected'?'image_decode':'image_load',
                errorName:clean(decoded[failedIndex].reason?.name),httpStatus:0});
              revealsFailed.set(file.id, attempt);
              // Other choices may still finish. Preserve their recovery evidence.
            }
            return;
          }
          if (!after.complete) return;
          if (revealsFailed.get(file.id) === attempt) { stopReveal(file.id); return; }
          const event = runtime.reveal(file.id, {attemptId:attempt, candidateCount:3,
            rankingStatus:clean(run.fields.rankingStatus) || (treatment() ? 'complete' : 'not_ranked')});
          if (event) revealsDone.set(file.id, attempt);
          stopReveal(file.id);
          durable(file, attempt);
        }
      } finally {
        if (revealRuns.get(file.id) === run) revealRuns.delete(file.id);
      }
    })();
    return run.promise;
  }
  function selection(file, model, source = 'inline') {
    if (!active() || !file || !clean(model)) return;
    captureChoice(file, model, true);
    const keys = file.ntAiOrder || file.bgrVisible || (treatment() ? ['ninja1','ninja2','ninja3','ninja4'] : ['ix','pc','gram']);
    record('selection', {designId:file.id, modelId:clean(file.bgrModelDetails?.[model]?.modelId) || model,
      position:Math.max(0, keys.indexOf(model)) + 1,
      recommended:model === (file.ntAiWinnerKey || (!treatment() ? 'ix' : null)), source});
  }
  function captureChoice(file, selected = file?.bgrModel, manual = false) {
    if (!active() || !file) return null;
    const details = file.bgrModelDetails?.[selected] || {};
    // Legacy engine keys are explicit identities, never ranked display positions.
    const legacy = {ix:'imgix-default',pc:'imgix-model-4',gram:'legacy-ideogram'};
    const ready = !file.ntAiPending && !!file.bgrVersions?.[selected];
    const modelId = ready ? clean(details.modelId) || (!treatment() ? legacy[selected] : undefined) : undefined;
    // The displayed order can differ from candidate IDs (and collapse after failures).
    const keys = file.ntBgrOptionKeys || file.bgrVisible || (treatment() ? file.ntAiOrder : ['ix','pc','gram']);
    const index = Array.isArray(keys) ? keys.slice(0,3).indexOf(selected) : -1;
    const finalRank = ready && index >= 0 ? String(index + 1) : 'unknown';
    return runtime.observeChoice(file.id, {on:file.bgRemove,modelId,finalRank,
      modelProvider:clean(details.provider) || (details.provider === 'Ninja AI' ? 'ninja_ai' :
        !treatment() && legacy[selected] ? 'legacy' : undefined),
      checkpointSha256:details.checkpointSha256}, {manual});
  }
  function lifecycle(name, detail = {}) {
    if (!active()) return;
    if (name === 'file-removed') { stopReveal(detail.id); runtime.cancelOperation?.(detail.id); }
    let attemptId, completed;
    if (name === 'upload-started') attemptId = startStage(detail.id, 'upload');
    if (name === 'file-uploaded' || name === 'file-failed') {
      attemptId = stageAttempts.get(`${detail.id}:upload`);
      completed = name === 'file-uploaded' ? completeStage(detail.id,'upload',attemptId) :
        stageFailure(detail.id,'upload','upload_failed',attemptId);
      if (!completed) return;
      if (name === 'file-failed') runtime.terminalFailure?.(detail.id,attemptId,'upload',
        cancelledStages.has(attemptId) ? 'upload_cancelled' : 'upload_failed');
    }
    if (name === 'cart-error') {
      attemptId = clean(detail.attemptId);
      if (!stageFailure(null,'cart','cart_request_failed',attemptId)) return;
      runtime.cartFailed?.(attemptId);
    }
    const map = {'upload-started':'upload_started', 'file-uploaded':'upload_succeeded',
      'file-failed':'upload_failed', 'file-rejected':'upload_failed', 'cart-error':'cart_error'};
    if (map[name]) record(map[name], {...uploadContexts.get(attemptId),designId:clean(detail.id),attemptId,durationMs:completed?.durationMs,
      reason:name === 'file-rejected' ? 'file_rejected' : name === 'file-failed' ? 'upload_failed' :
        name === 'cart-error' ? 'cart_request_failed' : undefined});
    // bgr-changed is deliberately absent: automatic commits are not buyer actions.
  }
  function startStage(id, stage, source) {
    if (!active()) return null;
    const attemptId = runtime.beginStage(id, stage, source);
    if (attemptId) stageAttempts.set(`${id}:${stage}`, attemptId);
    return attemptId;
  }
  function stageFailure(id, stage, reason, attemptId) {
    if (active() && attemptId) return runtime.failure(id, attemptId, stage, clean(reason) || 'unavailable');
    return null;
  }
  function completeStage(id, stage, attemptId) {
    if (active() && attemptId) return runtime.completeStage(id,attemptId,stage);
    return null;
  }
  function lineProps(properties, file) {
    if (!active() || !file) return;
    if (editor) throw new Error('Open the storefront to add items to your cart.');
    // Preview exclusion is retained in the existing browser assignment, not on the order.
    // Do not create an unmarked test cart if that internal record could not be saved.
    if (draft || assignment.source === 'storefront_default' && assignment.qa) {
      let saved;
      try { saved = JSON.parse(global.localStorage?.getItem(`nt-bgr-draft:${assignment.experimentKey}`) || 'null'); } catch (_) {}
      if (saved?.qa !== true || saved.experimentKey !== assignment.experimentKey) {
        throw new Error('Test storage is unavailable. Enable browser storage and try again.');
      }
    }
    // Model/default/toggle diagnostics stay in events. Reuse the builder's removal fields.
    captureChoice(file);
    delete properties._nt_bgr_qa;
    if (assignment.source === 'storefront_default') delete properties._nt_bgr_variation;
    else properties._nt_bgr_variation = assignment.variation;
  }
  function optionsPending(file) {
    if (!treatment() || !file?.bgRemove) return false;
    return (file.ntBgrOptionKeys || file.bgrVisible || file.ntAiOrder || []).slice(0,3).some(key =>
      !file.bgrVersions?.[key] && !file.bgrFailed?.[key] && !!(file.bgrDisplay?.[key] || file.bgrPending?.[key]));
  }
  function treatmentProperties(properties, file) {
    if (!treatment() || !file.bgRemove || !file.bgrModel) return properties;
    const chosen = file.bgrVersions?.[file.bgrModel];
    if (!chosen) return properties;
    const sep = chosen.includes('?') ? '&' : '?';
    // A saved file that already carries its displayBox `rect=` IS trimmed. imgix
    // runs trim BEFORE rect, so adding trim shifts the art by the rect offset and
    // clips it (NT-2829199, 2026-11-02). Same rule as bg-removal-multi.js.
    const trim = /[?&]rect=/.test(chosen) ? '' : `${sep}trim=colorUnlessAlpha&trim-tol=10`;
    const join = trim ? '&' : sep;
    properties['Upload (Vector Files Preferred)'] = `${chosen}${trim}${file.superRes ? `${join}upscale=true` : ''}`;
    properties._cartImg = `${chosen}${trim}${join}auto=compress&q=50&h=150`;
    properties['_Original Image'] ||= file.originalUploadReference || file.s3Url;
    const choice = captureChoice(file);
    properties['_BG Removal Service'] = ORDER_SERVICES.get(choice?.modelId) || 'Unknown';
    properties['_BG Removal Rank Choice'] = ['1','2','3'].includes(choice?.finalRank) ? `Rank ${choice.finalRank}` : 'Unknown';
    // Preserve displayed ranks, including gaps from failed candidates. Only
    // saved public outputs belong on the order; temporary previews cannot travel.
    const keys = (file.ntBgrOptionKeys || file.bgrVisible || file.ntAiOrder || []).slice(0,3);
    for (const [index,key] of keys.entries()) {
      const url = (file.ntAiOut && file.ntAiOut.run === file.bgrRun ? file.ntAiOut[key] : null) || file.ntMaskOrig?.[key] || file.bgrVersions?.[key];   // By Size v5: ranks record Ninja AI's own output, not the edited cut-out
      if (!url && !file.bgrFailed?.[key] && (file.bgrDisplay?.[key] || file.bgrPending?.[key])) {
        throw new Error('Wait for background removal to finish saving, then try again.');
      }
      const model = ORDER_SERVICES.get(file.bgrModelDetails?.[key]?.modelId);
      if (!model?.startsWith('ninja_ai_') || typeof url !== 'string') continue;
      let saved;
      try { saved = new URL(url); } catch (_) { continue; }
      if (saved.protocol !== 'https:' || saved.hostname !== 'ninjauploads-production.imgix.net' || saved.username || saved.password ||
          !/^\/image-ai-test____[a-f0-9]{64}\.png$/.test(saved.pathname)) continue;
      properties[`_Ninja AI Rank ${index+1} Version`] = url;
      properties[`_Ninja AI Rank ${index+1} Model`] = model;
    }
    return properties;
  }
  global.NtBgrThemeHooks = {boot, active, treatment, record, defaultFor, routedDefault, toggle,
    requested, durable, revealed, removalFinished, selection, captureChoice, lifecycle, lineProps, treatmentProperties, optionsPending, startStage, stageFailure, completeStage,
    terminalFailure:(id,attemptId,stage,reason)=>{if(active())return runtime.terminalFailure?.(id,attemptId,stage,clean(reason) || 'unavailable');},
    linkCart:(ids,attemptId)=>{if(active())runtime.linkCart?.(ids,attemptId);},
    failureDetail, storageErrorCode, uploadContext,
    blocked:(reason,attemptId) => {
      stageFailure(null,'validation',reason,attemptId);
      if (active()) runtime.blockedAttempt?.(attemptId);
      record('cart_blocked', {reason,attemptId});
    },
    validationResult:(attemptId,rejected,detail) => {
      if (rejected) {
        try {
          if (detail && active() && runtime.stagePending(null,attemptId,'validation')) record('fallback_detail',{
            attemptId,stage:'validation',reason:'file_rejected',source:'upload',operation:'file_validation',
            rejectionGuard:detail.rejectionGuard,fileBytes:detail.fileBytes,
            sourceExtension:detail.sourceExtension,mimeState:detail.mimeState});
        } catch (_) { /* Diagnostics cannot affect validation or its metric. */ }
        const failed = stageFailure(null,'validation','file_rejected',attemptId);
        if (failed) runtime.terminalFailure?.(null,attemptId,'validation','file_rejected');
        return failed;
      }
      return completeStage(null,'validation',attemptId);
    },
    currentAttempt:(file, attemptId) => !active() || attempts.get(file.id) === attemptId,
    failure:(file, stage, reason, attemptId = file?.ntBgrAttemptId) => {
      if (active()) runtime.failure(file?.id, attemptId, stage, clean(reason) || 'unavailable');
    },
    fallback:(file, reason, attemptId = file?.ntBgrAttemptId) => record('fallback',
      {designId:file?.id, attemptId, stage:'removal', reason:clean(reason) || 'unavailable'}),
    cartSuccess:(ids, count, attemptId, choices) => { if (active()) runtime.cartSuccess(ids, count, attemptId, choices); },
    uploadBlob:(...args) => { if (!active()) throw new Error('Local experiment is inactive'); return mocks.uploadBlob(...args); },
    cartRequest:items => { if (!active()) throw new Error('Local experiment is inactive'); return mocks.cart(items); },
    adapterSource:fallback => active() ? config.assets?.[treatment() ? 'treatment' : 'control'] || fallback : fallback};
  if (global.__ntBgrLocalConfig) boot(global.__ntBgrLocalConfig);
})(globalThis);
